import { Zoomable } from '@components/zoomable';
import { Button, IconButton, LinearProgress } from '@hyperflake/react-ui-library';
import {
    ArrowDownIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    ArrowUpIcon,
    CrossIcon,
    EditIcon,
    MaximizeIcon,
    MinimizeIcon,
    MinusIcon,
    PlusIcon,
    StopIcon,
} from '@icon/index';
import { Tooltip } from '@library/tooltip';
import useCctvCameraData from 'modules/cctv-cameras/hooks/useCctvCameraData';
import useCctvStatus from 'modules/cctv-cameras/hooks/useCctvStatus';
import { useStreamTokenRefresh } from 'modules/cctv-cameras/hooks/useStreamTokenRefresh';
import OverlayLoader from 'modules/reseller-cameras/components/ui/OverlayLoader';
import ContentViewer from 'modules/shared/features/ContentViewer/ContentViewer';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useNavigate, useParams } from 'react-router-dom';
import CctvCameraPlayer from '../CctvCameraPlayer/CctvCameraPlayer';
import CctvStatusBadge from '../CctvStatusBadge/CctvStatusBadge';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    applyPtzPreset,
    createPtzHomePreset,
    createPtzPreset,
    deletePtzPreset,
    getAllPresetOfChannel,
    updatePtzPan,
    updatePtzTilt,
    updatePtzZoom,
} from '@api/services/cctv-camera.service';
import { useEffect, useMemo, useState } from 'react';
import PtzButton from '../PtzButton/PtzButton';
import { PtzControlsPayload } from '@api/types/ptz-controls-payload.type';
import { Popover } from '@library/popover';
import { ICameraCCTVPreset, IProject } from '@api/models';
import ConfirmDeletePreset from '../ConfirmDeletePreset/ConfirmDeletePreset';
import { BackButton } from '@components/buttons';

const CctvCameraViewer = () => {
    const fullScreenHandle = useFullScreenHandle();
    const { clientId, projectId, cctvCameraId } = useParams();

    const { cctvCamera, isLoading } = useCctvCameraData(clientId, projectId, cctvCameraId);
    const { isLoading: liveLoading, setOnline, setOffline, status } = useCctvStatus();
    const [isPresetPopoverOpen, setIsPresetPopoverOpen] = useState(false);
    const [newPresetName, setNewPresetName] = useState('');
    const [deletingPresetId, setDeletingPresetId] = useState<string | null>(null);

    const navigate = useNavigate();

    // --- Channel selection ---
    const [selectedChannelNumber, setSelectedChannelNumber] = useState<string | null>(null);

    // Initialize selection: defaultChannelNumber -> first channel -> null
    useEffect(() => {
        if (!cctvCamera) return;
        const hasDefault = cctvCamera.channels?.some((ch) => ch.number === cctvCamera.defaultChannelNumber);
        setSelectedChannelNumber(
            hasDefault ? cctvCamera.defaultChannelNumber : (cctvCamera.channels?.[0]?.number ?? null)
        );
    }, [cctvCamera]);

    const activeChannel = useMemo(() => {
        if (!cctvCamera || !selectedChannelNumber) return undefined;
        return cctvCamera.channels?.find((ch) => ch.number === selectedChannelNumber);
    }, [cctvCamera, selectedChannelNumber]);

    // Prefer HLS; fallback to WebRTC; otherwise empty
    const activeHlsUrlSrc = useMemo(() => {
        if (!activeChannel) return '';
        return activeChannel.hlsUrl;
    }, [activeChannel]);
    const activeWebrtcUrl = useMemo(() => {
        if (!activeChannel) return '';
        return activeChannel.webrtcUrl;
    }, [activeChannel]);

    const activePtzAvailable = !!activeChannel?.ptzControl?.isAvailable;
    const noChannels = !cctvCamera?.channels?.length;

    // Stream token refresh
    const {
        hlsUrl: updatedHlsUrl,
        webrtcUrl: updatedWebrtcUrl,
        isLoading: isTokenLoading,
        isError: isTokenError,
        refetch: refetchToken,
    } = useStreamTokenRefresh(clientId, projectId, cctvCameraId, activeHlsUrlSrc, activeWebrtcUrl);

    const {
        data: presets = [],
        isLoading: isPresetsLoading,
        refetch: refetchPresets,
    } = useQuery({
        queryKey: ['ptz-presets', clientId, projectId, cctvCameraId, selectedChannelNumber],
        queryFn: () => getAllPresetOfChannel(clientId!, projectId!, cctvCameraId!, selectedChannelNumber!),
        enabled: !!clientId && !!projectId && !!cctvCameraId && !!selectedChannelNumber && activePtzAvailable,
    });

    const updatePtzCameraControlPanMutation = useMutation(
        (payload: Pick<PtzControlsPayload, 'pan'> & { channelNumber: string }) =>
            updatePtzPan(clientId!, projectId!, cctvCameraId!, payload)
    );
    //  Left right

    const updatePtzCameraControlTiltMutation = useMutation(
        (payload: Pick<PtzControlsPayload, 'tilt'> & { channelNumber: string }) =>
            updatePtzTilt(clientId!, projectId!, cctvCameraId!, payload)
    );
    // UP Down

    const updatePtzCameraControlZoomMutation = useMutation(
        (payload: Pick<PtzControlsPayload, 'zoom'> & { channelNumber: string }) =>
            updatePtzZoom(clientId!, projectId!, cctvCameraId!, payload)
    );

    const ensureChannelSelected = () => {
        if (!selectedChannelNumber) {
            toast.error('Please select a channel first.');
            return false;
        }
        return true;
    };

    const startPtzControlPan = async (values: Pick<PtzControlsPayload, 'pan'> & { channelNumber: string }) => {
        try {
            await updatePtzCameraControlPanMutation.mutateAsync(values);
            toast.success('Request sent successfully.');
        } catch (err: any) {
            toast.error(err.data.message || 'Some error occurred');
        }
    };

    const stopPtzControlPan = async (values: Pick<PtzControlsPayload, 'pan'> & { channelNumber: string }) => {
        try {
            await updatePtzCameraControlPanMutation.mutateAsync(values);
        } catch (err: any) {
            toast.error(err.data.message || 'Some error occurred');
        }
    };

    const startPtzControlTilt = async (values: Pick<PtzControlsPayload, 'tilt'> & { channelNumber: string }) => {
        try {
            await updatePtzCameraControlTiltMutation.mutateAsync(values);
            toast.success('Request sent successfully.');
        } catch (err: any) {
            toast.error(err.data.message || 'Some error occurred');
        }
    };

    const stopPtzControlTilt = async (values: Pick<PtzControlsPayload, 'tilt'> & { channelNumber: string }) => {
        try {
            await updatePtzCameraControlTiltMutation.mutateAsync(values);
        } catch (err: any) {
            toast.error(err.data.message || 'Some error occurred');
        }
    };

    const startPtzControlZoom = async (values: Pick<PtzControlsPayload, 'zoom'> & { channelNumber: string }) => {
        try {
            await updatePtzCameraControlZoomMutation.mutateAsync(values);
            toast.success('Request sent successfully.');
        } catch (err: any) {
            toast.error(err.data.message || 'Some error occurred');
        }
    };

    const stopPtzControlZoom = async (values: Pick<PtzControlsPayload, 'zoom'> & { channelNumber: string }) => {
        try {
            await updatePtzCameraControlZoomMutation.mutateAsync(values);
        } catch (err: any) {
            toast.error(err.data.message || 'Some error occurred');
        }
    };

    // STOP (send zeros to all)
    const stopPtz = async () => {
        if (!ensureChannelSelected()) return;
        try {
            await Promise.all([
                updatePtzCameraControlPanMutation.mutateAsync({ pan: 0, channelNumber: selectedChannelNumber! }),
                updatePtzCameraControlTiltMutation.mutateAsync({ tilt: 0, channelNumber: selectedChannelNumber! }),
                updatePtzCameraControlZoomMutation.mutateAsync({ zoom: 0, channelNumber: selectedChannelNumber! }),
            ]);
            toast.success('PTZ stopped');
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.data?.message || 'Stop failed');
        }
    };

    const createPresetMutation = useMutation({
        mutationFn: (presetName: string) =>
            createPtzPreset(clientId!, projectId!, cctvCameraId!, {
                presetName,
                channelNumber: selectedChannelNumber!,
            }),
    });

    const applyPresetMutation = useMutation((presetId: string) =>
        applyPtzPreset(clientId!, projectId!, cctvCameraId!, selectedChannelNumber!, presetId)
    );

    const deletePresetMutation = useMutation((presetId: string) =>
        deletePtzPreset(clientId!, projectId!, cctvCameraId!, presetId)
    );

    const createHomePresetMutation = useMutation({
        mutationFn: () =>
            createPtzHomePreset(clientId!, projectId!, cctvCameraId!, {
                channelNumber: selectedChannelNumber!,
            }),
    });

    const handleCreatePreset = () => {
        if (!ensureChannelSelected()) return;
        if (!newPresetName.trim()) {
            toast.error('Please enter a preset name.');
            return;
        }

        createPresetMutation.mutate(newPresetName.trim(), {
            onSuccess: async () => {
                toast.success('Preset created.');
                setNewPresetName('');
                await refetchPresets();
            },
            onError: (e: any) => {
                toast.error(e?.response?.data?.message || e?.data?.message || 'Failed to create preset');
            },
        });
    };

    const handleApplyPreset = async (preset: ICameraCCTVPreset) => {
        if (!ensureChannelSelected()) return;

        try {
            await applyPresetMutation.mutateAsync(preset._id);
            toast.success(`Preset "${preset.name}" applied.`);
            setIsPresetPopoverOpen(false);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.data?.message || 'Failed to apply preset');
        }
    };

    const handleDeletePreset = async (preset: ICameraCCTVPreset) => {
        if (!ensureChannelSelected()) return;

        try {
            setDeletingPresetId(preset._id); // ⭐ mark loading for this ID

            await deletePresetMutation.mutateAsync(preset._id);

            toast.success(`Preset "${preset.name}" deleted.`);
            await refetchPresets();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.data?.message || 'Failed to delete preset');
        } finally {
            setDeletingPresetId(null); // ⭐ reset
        }
    };

    const handleCreateHomePreset = async () => {
        if (!ensureChannelSelected()) return;

        try {
            await createHomePresetMutation.mutateAsync();
            toast.success('Home Preset Created.');
            await refetchPresets();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.data?.message || 'Failed to create preset');
        }
    };

    const handleStreamError = () => {
        setOffline();
        console.log('Stream error detected, attempting token refresh...');
        refetchToken();
    };

    return (
        <div>
            {isLoading ? (
                <LinearProgress color="primary" />
            ) : (
                <FullScreen handle={fullScreenHandle}>
                    <ContentViewer className="bg-black h-full">
                        <Zoomable>
                            <Zoomable.Container
                                className={
                                    fullScreenHandle.active ? 'h-screen' : 'h-[calc(100vh-var(--header-height))]'
                                }
                            >
                                <Zoomable.Content className="aspect-video max-w-full max-h-full">
                                    {noChannels ? (
                                        <div className="w-full h-full flex items-center justify-center text-white/80">
                                            No channels configured for this camera.
                                        </div>
                                    ) : activeHlsUrlSrc || activeWebrtcUrl ? (
                                        <CctvCameraPlayer
                                            hlsUrl={updatedHlsUrl || activeHlsUrlSrc}
                                            webrtcUrl={updatedWebrtcUrl || activeWebrtcUrl}
                                            className="w-full h-full"
                                            onData={setOnline}
                                            onError={handleStreamError}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-white/80">
                                            Selected channel has no playable URL.
                                        </div>
                                    )}
                                </Zoomable.Content>
                            </Zoomable.Container>

                            <div className="absolute bottom-6 left-[50%] translate-x-[-50%]">
                                <Zoomable.Controls />
                            </div>
                        </Zoomable>
                        <OverlayLoader show={liveLoading} />
                        <ContentViewer.Floating position="top-left">
                            <div className="flex justify-between gap-3 items-center">
                                <IconButton
                                    className="bg-white"
                                    onClick={() =>
                                        navigate(
                                            `/clients/${clientId}/projects/${projectId}/cctv-cameras/${cctvCameraId}/edit-cctv`
                                        )
                                    }
                                >
                                    <EditIcon />
                                </IconButton>

                                <CctvStatusBadge status={status} />
                            </div>
                        </ContentViewer.Floating>
                        {/* Channel selector + Fullscreen */}
                        <ContentViewer.Floating position="top-right">
                            <div className="flex items-center gap-2">
                                {!noChannels && (
                                    <>
                                        <label className="text-white text-sm">Channel</label>
                                        <select
                                            className="bg-white/90 rounded px-2 py-1 text-sm"
                                            value={selectedChannelNumber ?? ''}
                                            onChange={(e) => setSelectedChannelNumber(e.target.value)}
                                        >
                                            {(cctvCamera?.channels || []).map((ch) => (
                                                <option key={ch._id || ch.number} value={ch.number}>
                                                    {`CH ${ch.number}`}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}

                                {/* Fullscreen toggle */}
                                <Tooltip
                                    placement="left"
                                    content={fullScreenHandle.active ? 'Exit Fullscreen' : 'Fullscreen'}
                                >
                                    <IconButton
                                        color="warning"
                                        className="bg-white"
                                        onClick={
                                            fullScreenHandle.active ? fullScreenHandle.exit : fullScreenHandle.enter
                                        }
                                    >
                                        {fullScreenHandle.active ? (
                                            <MinimizeIcon className="text-grayscale-600" width="18" height="18" />
                                        ) : (
                                            <MaximizeIcon className="text-grayscale-600" width="18" height="18" />
                                        )}
                                    </IconButton>
                                </Tooltip>

                                {/* PTZ Preset Popover */}
                                {/* PTZ Preset Popover */}
                                {activePtzAvailable && (
                                    <Popover
                                        placement="bottom-start"
                                        open={isPresetPopoverOpen}
                                        setOpen={(open) => {
                                            setIsPresetPopoverOpen(open);
                                            if (open && selectedChannelNumber) {
                                                // ensure latest presets when opening
                                                refetchPresets();
                                            }
                                        }}
                                        contentClassName="px-4 py-3 rounded-2xl shadow-lg bg-white w-80"
                                        content={
                                            <div>
                                                {/* Header */}
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="text-sm text-grayscale-600 font-medium">
                                                        Manage Presets
                                                    </div>
                                                    <button onClick={() => setIsPresetPopoverOpen(false)}>
                                                        <CrossIcon className="w-3 h-3" />
                                                    </button>
                                                </div>

                                                {/* Input + Create button in the same row */}

                                                {isPresetsLoading ? (
                                                    <div className="py-4 text-center text-xs text-grayscale-400 mt-2">
                                                        Loading presets...
                                                    </div>
                                                ) : presets.length === 0 ? (
                                                    <p className="text-xs text-grayscale-400 mt-2">
                                                        No presets configured for this channel.
                                                    </p>
                                                ) : (
                                                    <div className="max-h-64 overflow-y-auto space-y-2 mt-2">
                                                        {presets.map((preset) => (
                                                            <div
                                                                key={preset._id}
                                                                className="flex items-center justify-between gap-2 border border-grayscale-200 rounded-lg px-3 py-2"
                                                            >
                                                                <span className="text-xs font-semibold">
                                                                    {preset.name}
                                                                </span>
                                                                <div className="flex gap-2">
                                                                    <Button
                                                                        size="xs"
                                                                        color="primary"
                                                                        onClick={() => handleApplyPreset(preset)}
                                                                        disabled={applyPresetMutation.isLoading}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                    <ConfirmDeletePreset
                                                                        presetName={preset.name}
                                                                        isLoading={deletingPresetId === preset._id} // ⭐ per-preset loading
                                                                        onConfirm={() => handleDeletePreset(preset)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-2 mt-4">
                                                    <input
                                                        type="text"
                                                        value={newPresetName}
                                                        onChange={(e) => setNewPresetName(e.target.value)}
                                                        placeholder="Preset name"
                                                        className="flex-1 border border-grayscale-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-primary-500"
                                                    />
                                                    <Button
                                                        size="xs"
                                                        color="primary"
                                                        onClick={handleCreatePreset}
                                                        disabled={createPresetMutation.isLoading}
                                                    >
                                                        {createPresetMutation.isLoading ? 'Saving…' : 'Create'}
                                                    </Button>
                                                </div>
                                                <Button
                                                    className="w-full mt-4"
                                                    size="xs"
                                                    color="primary"
                                                    type="button"
                                                    onClick={handleCreateHomePreset}
                                                >
                                                    Set Home Preset
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <Button size="sm" color="primary">
                                            Preset
                                        </Button>
                                    </Popover>
                                )}
                            </div>
                        </ContentViewer.Floating>
                        {/* PTZ controls (per selected channel) */}
                        {activePtzAvailable && (
                            <ContentViewer.Floating position="bottom-left">
                                <div className="flex items-end gap-4">
                                    <div className="flex flex-col">
                                        <div className="flex flex-col space-y-2 items-center ">
                                            {/* TOP */}
                                            <Tooltip placement="top" content="Tilt Up">
                                                <PtzButton
                                                    className=""
                                                    onMouseDown={() =>
                                                        startPtzControlTilt({
                                                            tilt: 1,
                                                            channelNumber: selectedChannelNumber,
                                                        })
                                                    }
                                                    onMouseUp={() =>
                                                        stopPtzControlTilt({
                                                            tilt: 0,
                                                            channelNumber: selectedChannelNumber,
                                                        })
                                                    }
                                                >
                                                    <ArrowUpIcon strokeWidth={2} height="12" className="text-white" />
                                                </PtzButton>
                                            </Tooltip>

                                            <div className="flex items-center justify-between space-x-2">
                                                {/* LEFT */}
                                                <Tooltip placement="top" content="Pan Left">
                                                    <PtzButton
                                                        onMouseDown={() =>
                                                            startPtzControlPan({
                                                                pan: -1,
                                                                channelNumber: selectedChannelNumber,
                                                            })
                                                        }
                                                        onMouseUp={() =>
                                                            stopPtzControlPan({
                                                                pan: 0,
                                                                channelNumber: selectedChannelNumber,
                                                            })
                                                        }
                                                    >
                                                        <ArrowLeftIcon
                                                            strokeWidth={1.5}
                                                            height="10"
                                                            className="text-white"
                                                        />
                                                    </PtzButton>
                                                </Tooltip>

                                                <Tooltip placement="top" content="Stop">
                                                    <IconButton
                                                        className="relative opacity-70"
                                                        onClick={() => {
                                                            stopPtzControlPan({
                                                                pan: 0,
                                                                channelNumber: selectedChannelNumber,
                                                            });
                                                            stopPtzControlTilt({
                                                                tilt: 0,
                                                                channelNumber: selectedChannelNumber,
                                                            });
                                                            stopPtzControlZoom({
                                                                zoom: 0,
                                                                channelNumber: selectedChannelNumber,
                                                            });
                                                        }}
                                                        color="secondary"
                                                    >
                                                        <StopIcon
                                                            strokeWidth={2.25}
                                                            width={18}
                                                            height={18}
                                                            className="text-white"
                                                        />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* RIGHT */}
                                                <Tooltip placement="top" content="Pan Right">
                                                    <PtzButton
                                                        onMouseDown={() =>
                                                            startPtzControlPan({
                                                                pan: 1,
                                                                channelNumber: selectedChannelNumber,
                                                            })
                                                        }
                                                        onMouseUp={() =>
                                                            stopPtzControlPan({
                                                                pan: 0,
                                                                channelNumber: selectedChannelNumber,
                                                            })
                                                        }
                                                    >
                                                        <ArrowRightIcon
                                                            strokeWidth={1.5}
                                                            height="10"
                                                            className="text-white"
                                                        />
                                                    </PtzButton>
                                                </Tooltip>
                                            </div>
                                            {/* BOTTOM */}
                                            <Tooltip placement="top" content="Tilt Down">
                                                <PtzButton
                                                    className=""
                                                    onMouseDown={() =>
                                                        startPtzControlTilt({
                                                            tilt: -1,
                                                            channelNumber: selectedChannelNumber,
                                                        })
                                                    }
                                                    onMouseUp={() =>
                                                        stopPtzControlTilt({
                                                            tilt: 0,
                                                            channelNumber: selectedChannelNumber,
                                                        })
                                                    }
                                                >
                                                    <ArrowDownIcon
                                                        strokeWidth={1.5}
                                                        height="12"
                                                        className="text-white"
                                                    />
                                                </PtzButton>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-start gap-3 ">
                                        <Tooltip placement="top" content="Optical Zoom In">
                                            <PtzButton
                                                className=""
                                                onMouseDown={() =>
                                                    startPtzControlZoom({
                                                        zoom: 1,
                                                        channelNumber: selectedChannelNumber,
                                                    })
                                                }
                                                onMouseUp={() =>
                                                    stopPtzControlZoom({
                                                        zoom: 0,
                                                        channelNumber: selectedChannelNumber,
                                                    })
                                                }
                                                delay={500}
                                            >
                                                <PlusIcon width="14" height="12" className="text-white" />
                                            </PtzButton>
                                        </Tooltip>
                                        <Tooltip placement="top" content="Optical Zoom Out">
                                            <PtzButton
                                                className=""
                                                onMouseDown={() =>
                                                    startPtzControlZoom({
                                                        zoom: -1,
                                                        channelNumber: selectedChannelNumber,
                                                    })
                                                }
                                                onMouseUp={() =>
                                                    stopPtzControlZoom({
                                                        zoom: 0,
                                                        channelNumber: selectedChannelNumber,
                                                    })
                                                }
                                                delay={500}
                                            >
                                                <MinusIcon width="14" height="3" className="text-white" />
                                            </PtzButton>
                                        </Tooltip>
                                    </div>
                                </div>
                            </ContentViewer.Floating>
                        )}
                    </ContentViewer>
                </FullScreen>
            )}
        </div>
    );
};

export default CctvCameraViewer;
